import * as THREE from 'three';
import { CUP_RIM_Y, GAME_CONFIG } from '../game/config';

export type AimReleaseHandler = (aimPoint: THREE.Vector3, power: number) => void;

const AIM_HALF_WIDTH = ((GAME_CONFIG.columns - 1) * GAME_CONFIG.cupSpacing) / 2;
const AIM_HALF_DEPTH = ((GAME_CONFIG.rows - 1) * GAME_CONFIG.cupSpacing) / 2;

export class AimController {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(CUP_RIM_Y + 0.05));
  private readonly aimPoint = new THREE.Vector3(0, CUP_RIM_Y + 0.05, GAME_CONFIG.boardCenter.z);
  private activePointerId: number | null = null;
  private chargeStartedAt = 0;
  private charging = false;
  private enabled = true;
  private keyboardChargeKey: 'Space' | 'Enter' | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly onRelease: AimReleaseHandler,
  ) {
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('pointercancel', this.handlePointerCancel);
    canvas.addEventListener('lostpointercapture', this.handleLostCapture);
    canvas.addEventListener('contextmenu', this.preventContextMenu);
    window.addEventListener('pointerup', this.handleWindowPointerUp);
    window.addEventListener('pointercancel', this.handleWindowPointerCancel);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.cancelCharge);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.cancelCharge();
  }

  cancel(): void {
    this.cancelCharge();
  }

  isCharging(): boolean {
    return this.charging;
  }

  getPower(now = performance.now()): number {
    if (!this.charging) return 0;
    return THREE.MathUtils.clamp((now - this.chargeStartedAt) / (GAME_CONFIG.chargeDuration * 1000), 0, 1);
  }

  copyAimPoint(target: THREE.Vector3): THREE.Vector3 {
    return target.copy(this.aimPoint);
  }

  setAimPoint(point: THREE.Vector3): void {
    this.aimPoint.set(
      THREE.MathUtils.clamp(point.x, -AIM_HALF_WIDTH, AIM_HALF_WIDTH),
      CUP_RIM_Y + 0.05,
      THREE.MathUtils.clamp(
        point.z,
        GAME_CONFIG.boardCenter.z - AIM_HALF_DEPTH,
        GAME_CONFIG.boardCenter.z + AIM_HALF_DEPTH,
      ),
    );
  }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.handlePointerMove);
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    this.canvas.removeEventListener('pointerup', this.handlePointerUp);
    this.canvas.removeEventListener('pointercancel', this.handlePointerCancel);
    this.canvas.removeEventListener('lostpointercapture', this.handleLostCapture);
    this.canvas.removeEventListener('contextmenu', this.preventContextMenu);
    window.removeEventListener('pointerup', this.handleWindowPointerUp);
    window.removeEventListener('pointercancel', this.handleWindowPointerCancel);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.cancelCharge);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (!this.enabled) return;
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
    if (this.activePointerId !== null) event.preventDefault();
    this.updateAimFromPointer(event.clientX, event.clientY);
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (!this.enabled || this.charging || event.button !== 0) return;
    event.preventDefault();
    this.updateAimFromPointer(event.clientX, event.clientY);
    this.activePointerId = event.pointerId;
    this.keyboardChargeKey = null;
    this.charging = true;
    this.chargeStartedAt = performance.now();
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic pointer events do not always support capture.
    }
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    event.preventDefault();
    this.updateAimFromPointer(event.clientX, event.clientY);
    this.releaseCharge();
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    event.preventDefault();
    this.cancelCharge();
  };

  private readonly handleWindowPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    event.preventDefault();
    this.updateAimFromPointer(event.clientX, event.clientY);
    this.releaseCharge();
  };

  private readonly handleWindowPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    this.cancelCharge();
  };

  private readonly handleLostCapture = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId && this.charging) this.cancelCharge();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled || this.isTypingTarget(event.target)) return;

    const step = event.shiftKey ? 0.34 : 0.17;
    if (event.code === 'ArrowLeft' || event.code === 'KeyA') this.aimPoint.x -= step;
    else if (event.code === 'ArrowRight' || event.code === 'KeyD') this.aimPoint.x += step;
    else if (event.code === 'ArrowUp' || event.code === 'KeyW') this.aimPoint.z -= step;
    else if (event.code === 'ArrowDown' || event.code === 'KeyS') this.aimPoint.z += step;
    else if ((event.code === 'Space' || event.code === 'Enter') && !event.repeat && !this.charging) {
      this.charging = true;
      this.chargeStartedAt = performance.now();
      this.keyboardChargeKey = event.code;
    } else {
      return;
    }

    this.setAimPoint(this.aimPoint);
    event.preventDefault();
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (event.code !== this.keyboardChargeKey) return;
    if (this.enabled && this.charging && this.activePointerId === null) {
      event.preventDefault();
      this.releaseCharge();
    } else {
      this.cancelCharge();
    }
  };

  private readonly handleVisibility = (): void => {
    if (document.hidden) this.cancelCharge();
  };

  private readonly cancelCharge = (): void => {
    const pointerId = this.activePointerId;
    this.charging = false;
    this.activePointerId = null;
    this.keyboardChargeKey = null;
    this.releasePointerCapture(pointerId);
  };

  private releaseCharge(): void {
    const power = Math.max(0.08, this.getPower());
    const pointerId = this.activePointerId;
    this.charging = false;
    this.activePointerId = null;
    this.keyboardChargeKey = null;
    this.releasePointerCapture(pointerId);
    this.onRelease(this.aimPoint.clone(), power);
  }

  private releasePointerCapture(pointerId: number | null): void {
    if (pointerId === null) return;
    try {
      if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId);
    } catch {
      // Capture may already be gone after pointer cancellation or a page transition.
    }
  }

  private readonly preventContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private updateAimFromPointer(clientX: number, clientY: number): void {
    const bounds = this.canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    this.pointer.set(
      ((clientX - bounds.left) / bounds.width) * 2 - 1,
      -((clientY - bounds.top) / bounds.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersection = this.raycaster.ray.intersectPlane(this.aimPlane, this.aimPoint);
    if (intersection) this.setAimPoint(intersection);
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return target.matches('input, textarea, select, button, [contenteditable="true"]');
  }
}
